import { createRoot } from "react-dom/client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ProgrammeInfoWindow } from "./GoogleMapPartials/ProgrammeInfoWindow";
import MapDirectionsBox from "./GoogleMapPartials/MapDirectionsBox";
import LocaliseIcon from "./Icons/LocaliseIcon";
import { Spinner } from "react-bootstrap";
import IconBus from "./Icons/IconBus";
import CarIcon from "./Icons/CarIcon";
import BikeIcon from "./Icons/BikeIcon";
import WalkIcon from "./Icons/WalkIcon";
import ResetIcon from "./Icons/ResetIcon";
import ChevronDownIcon from "./Icons/ChevronDownIcon";

const GMAPS_SCRIPT_ID = "google-maps-js";

function loadGoogleMaps({ apiKey, libraries = ["places"] }) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") return reject(new Error("No window"));
    if (window.google?.maps) return resolve(window.google);
    const existing = document.getElementById(GMAPS_SCRIPT_ID);
    if (existing) {
      const onLoad = () => resolve(window.google);
      const onError = () => reject(new Error("Google Maps script failed"));
      existing.addEventListener("load", onLoad, { once: true });
      existing.addEventListener("error", onError, { once: true });
      const t0 = Date.now();
      const poll = setInterval(() => {
        if (window.google?.maps) {
          clearInterval(poll);
          resolve(window.google);
        } else if (Date.now() - t0 > 15000) {
          clearInterval(poll);
          reject(new Error("Google Maps load timeout"));
        }
      }, 100);
      return;
    }
    const script = document.createElement("script");
    script.id = GMAPS_SCRIPT_ID;
    script.async = true;
    script.defer = true;
    const libs = encodeURIComponent(libraries.join(","));
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${libs}`;
    script.onload = () => resolve(window.google);
    script.onerror = () => reject(new Error("Google Maps script failed"));
    document.head.appendChild(script);
    const t0 = Date.now();
    const poll = setInterval(() => {
      if (window.google?.maps) {
        clearInterval(poll);
        resolve(window.google);
      } else if (Date.now() - t0 > 15000) {
        clearInterval(poll);
        reject(new Error("Google Maps load timeout"));
      }
    }, 100);
  });
}

const MAP_ICON_SVG_PINK = `
<svg xmlns="http://www.w3.org/2000/svg" width="56" height="62" viewBox="0 0 56 62" fill="none">
  <defs>
    <linearGradient id="g" x1="28" y1="4" x2="28" y2="54" gradientUnits="userSpaceOnUse"><stop stop-color="#D74166"/><stop offset="1" stop-color="#EE789F"/></linearGradient>
    <linearGradient id="i0" x1="-2.77576" y1="20.6303" x2="10.4364" y2="9.23637" gradientUnits="userSpaceOnUse"><stop stop-color="#F37241"/><stop offset="1" stop-color="#FABA5D"/></linearGradient>
    <linearGradient id="i1" x1="-0.121189" y1="18.4242" x2="12.9697" y2="7.15152" gradientUnits="userSpaceOnUse"><stop stop-color="#2A8AC6"/><stop offset=".2" stop-color="#2794BF"/><stop offset=".6" stop-color="#1FA4B6"/><stop offset=".8" stop-color="#1FAEAE"/><stop offset="1" stop-color="#1EB1AA"/></linearGradient>
    <linearGradient id="i2" x1="7.8424" y1="22.1455" x2="17.903" y2="13.297" gradientUnits="userSpaceOnUse"><stop stop-color="#ED3A3D"/><stop offset="1" stop-color="#F14E8D"/></linearGradient>
    <linearGradient id="i3" x1="10.4848" y1="19.8182" x2="22.7273" y2="9.15153" gradientUnits="userSpaceOnUse"><stop stop-color="#DDE04B"/><stop offset="1" stop-color="#9CCB5C"/></linearGradient>
    <linearGradient id="i4" x1="15.5394" y1="20.2667" x2="25.3576" y2="11.903" gradientUnits="userSpaceOnUse"><stop stop-color="#83796C"/><stop offset="1" stop-color="#CEA981"/></linearGradient>
    <clipPath id="c"><rect width="24" height="24"/></clipPath>
  </defs>
  <g>
  <path d="M43.5556 10.4341C47.5945 14.4666 49.9045 19.9096 49.9971 25.612C50.0897 31.3145 47.9577 36.8293 44.0518 40.9905L43.5556 41.5031L33.1841 51.857C31.8681 53.1702 30.1014 53.9348 28.2419 53.996C26.3824 54.0572 24.5692 53.4103 23.1695 52.1865L22.8175 51.857L12.4435 41.5006C8.3178 37.381 6 31.7935 6 25.9674C6 20.1413 8.3178 14.5538 12.4435 10.4341C16.5692 6.31441 22.1649 4 27.9996 4C33.8342 4 39.4299 6.31441 43.5556 10.4341Z" fill="#fff"/>
  <path d="M28 5C33.5696 5.00012 38.9107 7.20955 42.8486 11.1416C46.7037 14.9906 48.9086 20.1857 48.9971 25.6279C49.0854 31.0698 47.0508 36.333 43.3232 40.3047L42.8486 40.7949L32.4775 51.1494C31.3411 52.2833 29.8152 52.9432 28.209 52.9961C26.7031 53.0456 25.2325 52.558 24.0586 51.626L23.8281 51.4336L23.5156 51.1406L13.1504 40.793C9.21247 36.8608 7.00011 31.528 7 25.9678C7 20.4074 9.21237 15.0739 13.1504 11.1416C17.0884 7.20942 22.4302 5 28 5Z" stroke="url(#g)" stroke-width="2"/>
  </g>
  <g transform="translate(16 12)">
    <g clip-path="url(#c)">
      <path d="M5.33333 23.8788H0V8.60607L5.33333 3.27274V24V23.8788ZM0.606061 23.2727H4.72727V4.72728L0.606061 8.9697V23.3939V23.2727Z" fill="url(#i0)"/>
      <path d="M9.93942 23.8788H4.72729V0L9.93942 3.75758V24V23.8788ZM5.33336 23.2727H9.33336V4L5.33336 1.09091V23.2727Z" fill="url(#i1)"/>
      <path d="M14.6666 23.8788H9.33331V13.0909L14.6666 9.45456V23.8788ZM9.93937 23.2727H14.0606V10.6667L9.93937 13.4546V23.2727Z" fill="url(#i2)"/>
      <path d="M19.2727 23.8788H13.9394V5.09092L16.6061 3.39395L19.2727 5.09092V23.8788ZM14.6667 23.2727H18.7879V5.45456L16.7273 4.12122L14.6667 5.45456V23.3939V23.2727Z" fill="url(#i3)"/>
      <path d="M24 23.8788H18.7879V6.66666L24 10.303V23.8788ZM19.2728 23.2727H23.2728V10.5454L19.2728 7.75757V23.2727Z" fill="url(#i4)"/>
    </g>
  </g>
</svg>`;

const MAP_ICON_SVG_BLUE = `
<svg xmlns="http://www.w3.org/2000/svg" width="56" height="62" viewBox="0 0 56 62" fill="none">
  <defs>
    <linearGradient id="g" x1="28" y1="4" x2="28" y2="54" gradientUnits="userSpaceOnUse"><stop stop-color="#52B8B8"/><stop offset="1" stop-color="#3C8DDB"/></linearGradient>
    <linearGradient id="i0" x1="-2.77576" y1="20.6303" x2="10.4364" y2="9.23637" gradientUnits="userSpaceOnUse"><stop stop-color="#F37241"/><stop offset="1" stop-color="#FABA5D"/></linearGradient>
    <linearGradient id="i1" x1="-0.121189" y1="18.4242" x2="12.9697" y2="7.15152" gradientUnits="userSpaceOnUse"><stop stop-color="#2A8AC6"/><stop offset=".2" stop-color="#2794BF"/><stop offset=".6" stop-color="#1FA4B6"/><stop offset=".8" stop-color="#1FAEAE"/><stop offset="1" stop-color="#1EB1AA"/></linearGradient>
    <linearGradient id="i2" x1="7.8424" y1="22.1455" x2="17.903" y2="13.297" gradientUnits="userSpaceOnUse"><stop stop-color="#ED3A3D"/><stop offset="1" stop-color="#F14E8D"/></linearGradient>
    <linearGradient id="i3" x1="10.4848" y1="19.8182" x2="22.7273" y2="9.15153" gradientUnits="userSpaceOnUse"><stop stop-color="#DDE04B"/><stop offset="1" stop-color="#9CCB5C"/></linearGradient>
    <linearGradient id="i4" x1="15.5394" y1="20.2667" x2="25.3576" y2="11.903" gradientUnits="userSpaceOnUse"><stop stop-color="#83796C"/><stop offset="1" stop-color="#CEA981"/></linearGradient>
    <clipPath id="c"><rect width="24" height="24"/></clipPath>
  </defs>
  <g>
  <path d="M43.5556 10.4341C47.5945 14.4666 49.9045 19.9096 49.9971 25.612C50.0897 31.3145 47.9577 36.8293 44.0518 40.9905L43.5556 41.5031L33.1841 51.857C31.8681 53.1702 30.1014 53.9348 28.2419 53.996C26.3824 54.0572 24.5692 53.4103 23.1695 52.1865L22.8175 51.857L12.4435 41.5006C8.3178 37.381 6 31.7935 6 25.9674C6 20.1413 8.3178 14.5538 12.4435 10.4341C16.5692 6.31441 22.1649 4 27.9996 4C33.8342 4 39.4299 6.31441 43.5556 10.4341Z" fill="#fff"/>
  <path d="M28 5C33.5696 5.00012 38.9107 7.20955 42.8486 11.1416C46.7037 14.9906 48.9086 20.1857 48.9971 25.6279C49.0854 31.0698 47.0508 36.333 43.3232 40.3047L42.8486 40.7949L32.4775 51.1494C31.3411 52.2833 29.8152 52.9432 28.209 52.9961C26.7031 53.0456 25.2325 52.558 24.0586 51.626L23.8281 51.4336L23.5156 51.1406L13.1504 40.793C9.21247 36.8608 7.00011 31.528 7 25.9678C7 20.4074 9.21237 15.0739 13.1504 11.1416C17.0884 7.20942 22.4302 5 28 5Z" stroke="url(#g)" stroke-width="2"/>
  </g>
  <g transform="translate(16 12)">
    <g clip-path="url(#c)">
      <path d="M5.33333 23.8788H0V8.60607L5.33333 3.27274V24V23.8788ZM0.606061 23.2727H4.72727V4.72728L0.606061 8.9697V23.3939V23.2727Z" fill="url(#i0)"/>
      <path d="M9.93942 23.8788H4.72729V0L9.93942 3.75758V24V23.8788ZM5.33336 23.2727H9.33336V4L5.33336 1.09091V23.2727Z" fill="url(#i1)"/>
      <path d="M14.6666 23.8788H9.33331V13.0909L14.6666 9.45456V23.8788ZM9.93937 23.2727H14.0606V10.6667L9.93937 13.4546V23.2727Z" fill="url(#i2)"/>
      <path d="M19.2727 23.8788H13.9394V5.09092L16.6061 3.39395L19.2727 5.09092V23.8788ZM14.6667 23.2727H18.7879V5.45456L16.7273 4.12122L14.6667 5.45456V23.3939V23.2727Z" fill="url(#i3)"/>
      <path d="M24 23.8788H18.7879V6.66666L24 10.303V23.8788ZM19.2728 23.2727H23.2728V10.5454L19.2728 7.75757V23.2727Z" fill="url(#i4)"/>
    </g>
  </g>
</svg>`;

const svgToDataUrl = (svg) =>
  `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

const SingleProgramMap = ({ programme }) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markerRef = useRef(null);
  const infoWindowRef = useRef(null);
  const popupRootRef = useRef(null);
  const [mapsReady, setMapsReady] = useState(false);

  // Geocoding
  const geocoderRef = useRef(null);

  // Directions
  const directionsServiceRef = useRef(null);
  const directionsRendererRef = useRef(null);

  // Itinerary UI refs
  const originInputRef = useRef(null);
  const destinationInputRef = useRef(null);
  const originAutocompleteRef = useRef(null);
  const destinationAutocompleteRef = useRef(null);

  // Points
  const [origin, setOrigin] = useState(null);
  const [destination, setDestination] = useState(null);

  // Labels
  const [originLabel, setOriginLabel] = useState("");
  const [destinationLabel, setDestinationLabel] = useState("");

  // Route UI
  const [routeLegs, setRouteLegs] = useState(null);
  const [hasRoute, setHasRoute] = useState(false);
  const [isRouting, setIsRouting] = useState(false);
  const [travelMode, setTravelMode] = useState("DRIVING");
  const [showRouteBox, setShowRouteBox] = useState(false);

  // Localise buttons
  const [localiseALoading, setLocaliseALoading] = useState(false);
  const [localiseBLoading, setLocaliseBLoading] = useState(false);

  // Stable callback refs (avoid re-running heavy map init effect)
  const onSetPointARef = useRef(null);
  const onSetPointBRef = useRef(null);

  const getMyLocation = useCallback(
    () =>
      new Promise((resolve, reject) => {
        if (typeof window === "undefined")
          return reject(new Error("No window"));
        if (!navigator?.geolocation)
          return reject(new Error("Geolocation not supported"));
        if (!window.google?.maps)
          return reject(new Error("Google Maps not ready yet"));

        navigator.geolocation.getCurrentPosition(
          (pos) => {
            const latLng = new window.google.maps.LatLng(
              pos.coords.latitude,
              pos.coords.longitude,
            );
            resolve({ latLng, accuracy: pos.coords.accuracy });
          },
          (err) => reject(err),
          { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
        );
      }),
    [],
  );

  const reverseGeocode = useCallback(
    (latLng) =>
      new Promise((resolve) => {
        const geocoder = geocoderRef.current;
        if (!geocoder || !latLng) return resolve("");
        geocoder.geocode({ location: latLng }, (results, status) => {
          if (status === "OK" && results?.[0]?.formatted_address) {
            resolve(results[0].formatted_address);
          } else {
            resolve("");
          }
        });
      }),
    [],
  );

  const onSetPointA = useCallback(
    async ({ latLng, label }) => {
      if (!latLng) return;
      setShowRouteBox(true);
      setOrigin(latLng);
      let finalLabel = label || "";
      if (!finalLabel) {
        const addr = await reverseGeocode(latLng);
        finalLabel =
          addr || `${latLng.lat().toFixed(6)}, ${latLng.lng().toFixed(6)}`;
      }
      setOriginLabel(finalLabel);
      if (originInputRef.current) originInputRef.current.value = finalLabel;
    },
    [reverseGeocode],
  );

  const onSetPointB = useCallback(
    async ({ latLng, label }) => {
      if (!latLng) return;
      setShowRouteBox(true);
      setDestination(latLng);
      let finalLabel = label || "";
      if (!finalLabel) {
        const addr = await reverseGeocode(latLng);
        finalLabel =
          addr || `${latLng.lat().toFixed(6)}, ${latLng.lng().toFixed(6)}`;
      }
      setDestinationLabel(finalLabel);
      if (destinationInputRef.current)
        destinationInputRef.current.value = finalLabel;
    },
    [reverseGeocode],
  );

  // Keep refs in sync
  useEffect(() => {
    onSetPointARef.current = onSetPointA;
  }, [onSetPointA]);
  useEffect(() => {
    onSetPointBRef.current = onSetPointB;
  }, [onSetPointB]);

  const localiseToA = useCallback(async () => {
    if (!mapsReady) return;
    try {
      setLocaliseALoading(true);
      const { latLng } = await getMyLocation();
      await onSetPointA({ latLng, label: "Ma position" });
      const map = mapInstanceRef.current;
      if (map) {
        map.panTo(latLng);
        const z = map.getZoom();
        if (typeof z === "number" && z < 13) map.setZoom(13);
      }
    } catch (e) {
      console.error("Geolocation A failed:", e);
    } finally {
      setLocaliseALoading(false);
    }
  }, [mapsReady, getMyLocation, onSetPointA]);

  const localiseToB = useCallback(async () => {
    if (!mapsReady) return;
    try {
      setLocaliseBLoading(true);
      const { latLng } = await getMyLocation();
      await onSetPointB({ latLng, label: "Ma position" });
      const map = mapInstanceRef.current;
      if (map) {
        map.panTo(latLng);
        const z = map.getZoom();
        if (typeof z === "number" && z < 13) map.setZoom(13);
      }
    } catch (e) {
      console.error("Geolocation B failed:", e);
    } finally {
      setLocaliseBLoading(false);
    }
  }, [mapsReady, getMyLocation, onSetPointB]);

  const buildRoute = useCallback(() => {
    const map = mapInstanceRef.current;
    const service = directionsServiceRef.current;
    const renderer = directionsRendererRef.current;
    if (!map || !service || !renderer) return;
    if (!origin || !destination) return;
    setIsRouting(true);
    const request = {
      origin,
      destination,
      travelMode: window.google.maps.TravelMode[travelMode],
    };
    service.route(request, (result, status) => {
      setIsRouting(false);
      if (status === "OK" && result) {
        renderer.setDirections(result);
        const leg = result?.routes?.[0]?.legs?.[0] || null;
        setRouteLegs(leg);
        setHasRoute(true);
      } else {
        renderer.setDirections({ routes: [] });
        setRouteLegs(null);
        setHasRoute(false);
      }
    });
  }, [origin, destination, travelMode]);

  const buildRouteRef = useRef(buildRoute);
  useEffect(() => {
    buildRouteRef.current = buildRoute;
  }, [buildRoute]);

  // Recalc route when travel mode changes and route already exists
  const prevTravelModeRef = useRef(travelMode);
  useEffect(() => {
    if (prevTravelModeRef.current === travelMode) return;
    prevTravelModeRef.current = travelMode;
    if (!hasRoute) return;
    buildRouteRef.current();
  }, [travelMode, hasRoute]);

  const resetRoute = useCallback(() => {
    directionsRendererRef.current?.setDirections({ routes: [] });
    setHasRoute(false);
    setRouteLegs(null);
    setIsRouting(false);
    setOrigin(null);
    setDestination(null);
    setOriginLabel("");
    setDestinationLabel("");
    if (originInputRef.current) originInputRef.current.value = "";
    if (destinationInputRef.current) destinationInputRef.current.value = "";
  }, []);

  // Load Google Maps
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (window.google?.maps) {
          if (!cancelled) setMapsReady(true);
          return;
        }
        await loadGoogleMaps({
          apiKey: "AIzaSyA4m5vfwsuRsuPqKLPuFUmm-niBsZ0WXuY",
          libraries: ["places"],
        });
        if (!cancelled) setMapsReady(true);
      } catch (e) {
        console.error("Google Maps load error:", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Init map + marker + always-open InfoWindow
  useEffect(() => {
    if (!mapsReady || !mapRef.current) return;
    if (!programme) return;

    const lat = Number(programme?.latitude ?? programme?.lat);
    const lng = Number(programme?.longitude ?? programme?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

    const center = { lat, lng };

    // Create map only once
    if (!mapInstanceRef.current) {
      mapInstanceRef.current = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 14,
        gestureHandling: "greedy",
        scrollwheel: true,
      });
    } else {
      mapInstanceRef.current.setCenter(center);
    }

    const map = mapInstanceRef.current;

    // Clean up previous marker & infowindow
    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
      infoWindowRef.current = null;
    }
    if (popupRootRef.current) {
      popupRootRef.current.unmount();
      popupRootRef.current = null;
    }

    const isRp = programme?.isRp;
    const iconUrl = svgToDataUrl(isRp ? MAP_ICON_SVG_PINK : MAP_ICON_SVG_BLUE);

    const marker = new window.google.maps.Marker({
      position: center,
      map,
      icon: {
        url: iconUrl,
        scaledSize: new window.google.maps.Size(44, 50),
        anchor: new window.google.maps.Point(22, 50),
      },
    });
    markerRef.current = marker;

    // Create InfoWindow with ProgrammeInfoWindow always open
    const iw = new window.google.maps.InfoWindow();
    infoWindowRef.current = iw;

    const container = document.createElement("div");
    iw.setContent(container);
    iw.open({ map, anchor: marker });

    const root = createRoot(container);
    popupRootRef.current = root;

    const documentId = programme?.documentId;

    root.render(
      <ProgrammeInfoWindow
        documentId={documentId}
        markerLat={lat}
        markerLng={lng}
        onSetPointA={(...args) => onSetPointARef.current?.(...args)}
        onSetPointB={(...args) => onSetPointBRef.current?.(...args)}
      />,
    );

    // Re-open if user closes it
    window.google.maps.event.addListener(iw, "closeclick", () => {
      setTimeout(() => {
        iw.open({ map, anchor: marker });
      }, 100);
    });

    // Click on map => set Point A
    const mapClickListener = map.addListener("click", async (e) => {
      if (!e?.latLng) return;
      await onSetPointARef.current?.({ latLng: e.latLng });
    });

    return () => {
      if (mapClickListener) {
        window.google.maps.event.removeListener(mapClickListener);
      }
      if (popupRootRef.current) {
        popupRootRef.current.unmount();
        popupRootRef.current = null;
      }
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
        infoWindowRef.current = null;
      }
      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }
    };
  }, [mapsReady, programme]);

  // Geocoder init
  useEffect(() => {
    if (!mapsReady) return;
    if (!geocoderRef.current)
      geocoderRef.current = new window.google.maps.Geocoder();
  }, [mapsReady]);

  // Directions init
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!mapsReady || !map) return;
    if (!directionsServiceRef.current) {
      directionsServiceRef.current = new window.google.maps.DirectionsService();
    }
    if (!directionsRendererRef.current) {
      directionsRendererRef.current = new window.google.maps.DirectionsRenderer(
        {
          map,
          suppressMarkers: false,
          preserveViewport: false,
        },
      );
    }
  }, [mapsReady]);

  // Autocomplete init
  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!mapsReady || !map) return;
    if (!window.google?.maps?.places) return;

    if (originInputRef.current && !originAutocompleteRef.current) {
      originAutocompleteRef.current =
        new window.google.maps.places.Autocomplete(originInputRef.current, {
          fields: ["geometry", "name", "formatted_address"],
        });
      originAutocompleteRef.current.addListener("place_changed", async () => {
        const p = originAutocompleteRef.current.getPlace();
        if (!p?.geometry?.location) return;
        const label = p.formatted_address || p.name || "";
        await onSetPointARef.current?.({ latLng: p.geometry.location, label });
      });
    }

    if (destinationInputRef.current && !destinationAutocompleteRef.current) {
      destinationAutocompleteRef.current =
        new window.google.maps.places.Autocomplete(
          destinationInputRef.current,
          {
            fields: ["geometry", "name", "formatted_address"],
          },
        );
      destinationAutocompleteRef.current.addListener(
        "place_changed",
        async () => {
          const p = destinationAutocompleteRef.current.getPlace();
          if (!p?.geometry?.location) return;
          const label = p.formatted_address || p.name || "";
          await onSetPointBRef.current?.({
            latLng: p.geometry.location,
            label,
          });
        },
      );
    }
  }, [mapsReady]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (popupRootRef.current) {
        popupRootRef.current.unmount();
        popupRootRef.current = null;
      }
    };
  }, []);

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      {/* ROUTE BOX - inline overlay */}
      <div
        style={{
          position: "absolute",
          top: 10,
          left: 10,
          zIndex: 30,
          width: 320,
          maxHeight: "calc(100% - 50px)",
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <div
          className="border mb-2"
          style={{
            transition: "all 0.2s ease-in-out",
            width: "100%",
            background: "white",
            borderRadius: 12,
            padding: 10,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {showRouteBox && (
            <div style={{ position: "relative" }}>
              <input
                ref={originInputRef}
                placeholder="Point A"
                style={{
                  width: "100%",
                  height: 40,
                  borderRadius: 10,
                  border: "1px solid #e5e7eb",
                  padding: "0 40px 0 12px",
                  outline: "none",
                }}
                defaultValue={originLabel}
              />
              <button
                type="button"
                onClick={localiseToA}
                title="Utiliser ma position (Point A)"
                style={{
                  position: "absolute",
                  right: 6,
                  top: 6,
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background: "white",
                  cursor: "pointer",
                }}
              >
                {localiseALoading ? <Spinner size="sm" /> : <LocaliseIcon />}
              </button>
            </div>
          )}

          {showRouteBox && (
            <div style={{ position: "relative" }}>
              <input
                ref={destinationInputRef}
                placeholder="Point B"
                style={{
                  width: "100%",
                  height: 40,
                  borderRadius: 10,
                  border: "1px solid #e5e7eb",
                  padding: "0 40px 0 12px",
                  outline: "none",
                }}
                defaultValue={destinationLabel}
              />
              <button
                type="button"
                onClick={localiseToB}
                title="Utiliser ma position (Point B)"
                style={{
                  position: "absolute",
                  right: 6,
                  top: 6,
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background: "white",
                  cursor: "pointer",
                }}
              >
                {localiseBLoading ? <Spinner size="sm" /> : <LocaliseIcon />}
              </button>
            </div>
          )}

          {showRouteBox && (
            <div style={{ display: "flex", gap: 8 }}>
              {[
                {
                  k: "TRANSIT",
                  label: (
                    <IconBus
                      fill={travelMode === "TRANSIT" ? "white" : "#111827"}
                    />
                  ),
                },
                {
                  k: "DRIVING",
                  label: (
                    <CarIcon
                      fill={travelMode === "DRIVING" ? "white" : "#111827"}
                    />
                  ),
                },
                {
                  k: "BICYCLING",
                  label: (
                    <BikeIcon
                      fill={travelMode === "BICYCLING" ? "white" : "#111827"}
                    />
                  ),
                },
                {
                  k: "WALKING",
                  label: (
                    <WalkIcon
                      fill={travelMode === "WALKING" ? "white" : "#111827"}
                    />
                  ),
                },
              ].map((m) => (
                <button
                  key={m.k}
                  type="button"
                  onClick={() => setTravelMode(m.k)}
                  style={{
                    flex: 1,
                    height: 40,
                    borderRadius: 10,
                    border: "1px solid #e5e7eb",
                    background: travelMode === m.k ? "#111827" : "white",
                    color: travelMode === m.k ? "white" : "#111827",
                    cursor: "pointer",
                    fontSize: 18,
                  }}
                >
                  {m.label}
                </button>
              ))}
            </div>
          )}

          <div className="d-flex gap-2">
            <button
              type="button"
              className="flex-1"
              onClick={buildRoute}
              disabled={!origin || !destination || isRouting}
              style={{
                height: 42,
                borderRadius: 10,
                border: "none",
                background: "#2563eb",
                color: "white",
                fontWeight: 700,
                cursor: "pointer",
                opacity: !origin || !destination || isRouting ? 0.6 : 1,
              }}
            >
              {isRouting ? "Calcul…" : "Obtenir l'itinéraire"}
            </button>

            {showRouteBox ? (
              <button
                type="button"
                onClick={() => resetRoute()}
                disabled={(!origin && !destination && !hasRoute) || isRouting}
                title="Réinitialiser"
                className="border"
                style={{
                  width: 46,
                  height: 42,
                  borderRadius: 10,
                  background: "white",
                  cursor: "pointer",
                  opacity:
                    (!origin && !destination && !hasRoute) || isRouting
                      ? 0.6
                      : 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <ResetIcon />
              </button>
            ) : null}
            <button
              type="button"
              onClick={() => setShowRouteBox(!showRouteBox)}
              title="Afficher/Masquer"
              className={`border ${showRouteBox ? "rotatedCloseIcon" : ""}`}
              style={{
                outline: "none",
                width: 46,
                height: 42,
                borderRadius: 10,
                background: "white",
                cursor: "pointer",
                opacity:
                  (!origin && !destination && !hasRoute) || isRouting ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ChevronDownIcon />
            </button>
          </div>

          {showRouteBox ? <MapDirectionsBox legs={routeLegs} /> : null}
        </div>
      </div>

      {/* MAP */}
      <div
        ref={mapRef}
        className="radius10"
        style={{
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      />
    </div>
  );
};

export default SingleProgramMap;
